/* eslint react/jsx-no-target-blank: 0 */
import * as React from 'react';
import Text from '../../../shared/Text';
import {
  LoginSetupTotpAddAccountDetails,
  LoginStepTotpCodeInputArea,
  LoginSetupTotpDetails,
} from './step-totp-details';
import { encodeTotpKeyUri } from '../../utils/totp-key-uri-encoding';
import { useAuthData, useResetForm, useSetAuthData, useSubmitLogin } from '../providers/auth/hooks';
import { Box, Button, Step, StepContent, StepLabel, Stepper, Typography } from '@mui/material';
import { FormFieldWrapper } from '../shared-form-components';
import { AsyncButton } from '@c42/component-library';
import { AsyncState, isError, isLoading } from '@c42/constants';
import { POST, request } from '../../utils/request';
import { API_TOTP_COMPLETE_CONFIG_PATH } from '../providers/auth/constants';

const LoginStepTotpConfig: React.FC = () => {
  const [submitState, setSubmitState] = React.useState(AsyncState.NOT_STARTED);

  const resetForm = useResetForm();
  const setAuthData = useSetAuthData();
  const submitLogin = useSubmitLogin();

  const { password, totp, username, totpSecret, isAuthenticating } = useAuthData();
  const isFormDisabled = isLoading(submitState) || isAuthenticating;
  const totpKeyUri = encodeTotpKeyUri({
    account: username,
    secret: totpSecret,
  });

  const submitForm = async (e: React.FormEvent<any>) => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
    }

    try {
      setSubmitState(AsyncState.LOADING);
      await request(API_TOTP_COMPLETE_CONFIG_PATH, POST, {
        body: JSON.stringify({
          username,
          password,
          totp: Number(totp),
        }),
        headers: {
          'content-type': 'application/json',
        },
      });
      submitLogin();
    } catch (e) {
      setSubmitState(AsyncState.ERROR);
    }
  };

  const resetFormHandler = (e: React.MouseEvent<any>) => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
    }
    resetForm();
  };

  const changeTotpHandler: React.FormEventHandler<any> = (event) => {
    event.persist?.();
    setAuthData((current) => ({
      ...current,
      totp: (event.target as HTMLInputElement).value,
    }));
  };

  return (
    <Box mt={3} mx={6} width="fit-content">
      <form data-test="login-step-totp-config-form" name="loginFrm" noValidate onSubmit={submitForm}>
        <Typography variant="h5">{Text.get('login_totp_config_headline')}</Typography>

        <Typography sx={{ mt: 1 }} variant="body2">
          {Text.get('login_totp_config_description')}
        </Typography>

        <Stepper orientation="vertical">
          <Step active completed={false}>
            <StepLabel>
              <Typography variant="h5">{Text.get('login_totp_config_step1_headline')}</Typography>
            </StepLabel>
            <StepContent>
              <LoginSetupTotpDetails />
            </StepContent>
          </Step>
          <Step active completed={false}>
            <StepLabel>
              <Typography variant="h5">{Text.get('login_totp_config_step2_headline')}</Typography>
            </StepLabel>
            <StepContent>
              <LoginSetupTotpAddAccountDetails totpSecret={totpSecret} totpKeyUri={totpKeyUri} />
            </StepContent>
          </Step>
          <Step active completed={false}>
            <StepLabel>
              <Typography variant="h5">{Text.get('login_totp_config_step3_headline')}</Typography>
            </StepLabel>
            <StepContent>
              <LoginStepTotpCodeInputArea
                isTotpInvalid={isError(submitState)}
                isFormDisabled={isFormDisabled}
                onChangeTotp={changeTotpHandler}
              />
            </StepContent>
          </Step>
        </Stepper>

        <FormFieldWrapper mb={3} mt={5}>
          <AsyncButton
            data-test="submit"
            disabled={isFormDisabled}
            isLoading={isLoading(submitState)}
            type="submit"
            variant="contained"
          >
            {Text.get('login_button_sign_in')}
          </AsyncButton>
        </FormFieldWrapper>

        <FormFieldWrapper>
          <Button data-test="signin-different-account" onClick={resetFormHandler} size="small" variant="text">
            {Text.get('sso_signin_different_account')}
          </Button>
        </FormFieldWrapper>
      </form>
    </Box>
  );
};

export default LoginStepTotpConfig;
